import React, { useContext, createContext } from 'react';
import { Checkbox } from '@zoopla-eng/zds-components/checkbox';
import { getPropertyType } from '@zoopla/listing-search/get-property-type';
import { SelectGroup } from './SelectGroup';
import { SearchBarContext } from './SearchBar';
import { RadioGroup } from '@zoopla-eng/zds-components/radio-group';

const propertyTypes = ['property', 'houses', 'flats', 'farms_land'] as const;

const propertySubTypeOptions: Record<string, string> = {
    detached: 'Detached',
    semi_detached: 'Semi-detached',
    terraced: 'Terraced',
    flats: 'Flats',
    bungalow: 'Bungalows',
    farms_land: 'Farms/land',
    park_home: 'Park homes'
};

const propertySubTypes = [
    'detached',
    'semi_detached',
    'terraced',
    'flats',
    'bungalow',
    'farms_land',
    'park_home'
] as const;

export type PropertyType = (typeof propertyTypes)[number];
export type PropertySubTypes = Array<(typeof propertySubTypes)[number]>;

type PropertyTypeValue = {
    propertyType: string | string[];
    propertySubType: string[];
};

type PropertyTypeContext = {
    value: PropertyTypeValue;
    onChange?: (props: PropertyTypeValue) => void;
    dropdownValue: string;
};

const initialContextValue: PropertyTypeContext = {
    value: {
        propertyType: '',
        propertySubType: []
    },
    onChange: undefined,
    dropdownValue: 'Show all'
};

const PropertyTypesContext =
    createContext<PropertyTypeContext>(initialContextValue);

export const SelectPropertyType: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const { formValue, updateFormValue } = useContext(SearchBarContext);
    const value = {
        propertyType: formValue.property_type,
        propertySubType: formValue.property_sub_type
    };

    const onChange = ({ propertyType, propertySubType }: PropertyTypeValue) => {
        updateFormValue?.((oldFormValue) => ({
            ...oldFormValue,
            property_sub_type: propertySubType,
            property_type: propertyType
        }));
    };

    let dropdownValue = 'Show all';

    if (value.propertySubType.length > 0) {
        dropdownValue = value.propertySubType
            .map((subType) => propertySubTypeOptions[subType])
            .join(', ');
    }

    return (
        <PropertyTypesContext.Provider
            value={{ value, onChange, dropdownValue }}
        >
            {children}
        </PropertyTypesContext.Provider>
    );
};

export const RadioPropertyType = () => {
    const { value, onChange } = useContext(PropertyTypesContext);

    return (
        <RadioGroup
            bisect
            id={'property-type'}
            label={'Property Type'}
            value={value.propertyType}
            items={[
                {
                    id: 'property',
                    value: 'property',
                    label: 'Show all'
                },
                {
                    id: 'houses',
                    value: 'houses',
                    label: 'Houses'
                },
                {
                    id: 'flats',
                    value: 'flats',
                    label: 'Flats'
                }
            ]}
            onValueChange={(value) => {
                if (onChange) {
                    let propertySubType: PropertySubTypes = [];

                    switch (value) {
                        case 'houses':
                            propertySubType = [
                                'detached',
                                'semi_detached',
                                'terraced'
                            ];
                            break;
                        case 'flats':
                            propertySubType = ['flats'];
                            break;
                        default:
                            break;
                    }

                    onChange({
                        propertyType: value,
                        propertySubType: propertySubType
                    });
                }
            }}
        />
    );
};

export const CheckboxPropertyTypeShowAll = () => {
    const { value, onChange } = useContext(PropertyTypesContext);

    return (
        <Checkbox
            name="show_all"
            label="Show all"
            checked={value.propertySubType.length === 0}
            onCheckedChange={() => {
                onChange &&
                    onChange({
                        propertySubType: [],
                        propertyType: 'property'
                    });
            }}
        />
    );
};

export const CheckboxPropertyType = ({
    name
}: {
    name: PropertySubTypes[number];
}) => {
    const { value, onChange } = useContext(PropertyTypesContext);

    return (
        <Checkbox
            name={`property-type-${String(name)}`}
            label={propertySubTypeOptions[name]}
            checked={value.propertySubType.includes(name)}
            onCheckedChange={(checked) => {
                let _propertySubType: string[];

                if (checked) {
                    _propertySubType = [...value.propertySubType, name];
                } else {
                    _propertySubType = value.propertySubType.filter(
                        (i) => i !== name
                    );
                }

                onChange &&
                    onChange({
                        propertySubType: _propertySubType,
                        propertyType: getPropertyType(
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            _propertySubType as PropertySubTypes
                        )
                    });
            }}
        />
    );
};

export const SelectGroupProperty: React.FC<{
    children: React.ReactNode;
    align?: 'left' | 'right';
}> = ({ children, align = 'left' }) => {
    const { dropdownValue } = useContext(PropertyTypesContext);

    return (
        <SelectGroup title="Property type" value={dropdownValue} align={align}>
            {children}
        </SelectGroup>
    );
};
